/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import https from 'https';
import getConfig from '../../config';
import * as url from 'url';

declare let adobeIMS: AdobeIMS;
class LinksService {
	/**
	 * Retrieves url for XD artifacts .
	 * @returns object - search datarequest response object
	 */
	fetchLink(urn) {
		return this._performLinksServiceRequest(urn);
	}

	saveToLibrary(bookmarkId) {
		return new Promise((resolve, reject) => {
			this._performPubLinksServiceRequest(bookmarkId, 'operation=copy')
				.then((data) => {
					if (data && data.monitorUrl) {
						this.monitor(data.monitorUrl, bookmarkId)
							.then(result => resolve(result))
							.catch(error => reject(error));
					}
				})
				.catch(error => reject(error));
		});
	}

	followLibrary(bookmarkId) {
		return new Promise((resolve, reject) => {
			this._performBookmarkServiceRequest(bookmarkId)
				.then(result => resolve(result))
				.catch(error => reject(error));
		});
	}

	monitor(url, bookmarkId) {
		const request = (url: string, resolve, reject) => {
			this._performPubLinksMonitorServiceRequest(url)
				.then((data) => {
					if (data.statusCode === 202) {
						setTimeout(() => {
							request(data.res.href, resolve, reject);
						}, data.res.retryAfter * 1000);
					}
					else if (data.statusCode === 200) {
						return resolve(data.res);
					}
					else {
						const error = new Error('_performPubLinksMonitorServiceRequest error');
						return reject({bookmarkId, error});
					}
				})
				.catch(error => reject(error));
		};

		return new Promise((resolve, reject) => {
			request(url, resolve, reject);
		});
	}

	/*
	 **
	 **  P R I V A T E   M E T H O D S
	 **
	 */
	static instance: LinksService;

	constructor() {
		if (!LinksService.instance) {
			LinksService.instance = this;
		}
		return LinksService.instance;
	}

	/**
	 * Retrieves Links data for XD artifacts from Links Service.
	 * @param {assetID} asset id
	 * @returns {Object}
	 */
	_performLinksServiceRequest(urn: string) {
		return new Promise((resolve) => {
			const request = https.request(
				{
					hostname: getConfig('LINKS_SERVICE_URI'),
					path: `/${urn}`,
					method: 'GET',
					headers: {
						'x-api-key': getConfig('CLIENT_ID'),
						authorization: `Bearer ${adobeIMS.getAccessToken()}`,
					},
				},
				(res) => {
					if (res.statusCode && res.statusCode >= 300) {
						window.cchome.util.logger.error('Links Service', 'Problem with Links Service request', res);
						const linksServiceError = new Error(`${res.statusCode}`);
						resolve(linksServiceError);
					}
					else {
						let data = '';
						// set the encoding to UTF8 to prevent decoding issues on certain browsers.
						res.setEncoding('utf8');
						res.on('data', chunk => (data += chunk));
						res.on('end', () => {
							// Success!
							try {
								resolve(JSON.parse(data));
							}
							catch (e) {
								resolve(data);
							}
						});
					}
				},
			);

			request.on('error', (err) => {
				// on error, display the search error message
				window.cchome.util.logger.error('Links Service', 'Problem with Links Service request', err.toString());
				const linksServiceError = new Error('_performLinksServiceRequest error');
				resolve(linksServiceError);
			});
			request.end();
		});
	}

	/**
	 * Request copy to library.
	 * @param {id} public asset id
	 * @param {param} prameters for the actions in https://git.corp.adobe.com/pages/CPX/apidocs/#/publinks
	 * @returns {Object}
	 */
	_performPubLinksServiceRequest(id: string, param: string) {
		return new Promise<PubLinksServiceResponse>((resolve, reject) => {
			const request = https.request(
				{
					hostname: getConfig('LINKS_SERVICE'),
					path: `/api/v1/publinks/${id}?${param}`,
					method: 'POST',
					headers: {
						'x-api-key': getConfig('CLIENT_ID'),
						authorization: `Bearer ${adobeIMS.getAccessToken()}`,
					},
				},
				(res) => {
					if (res.statusCode && res.statusCode >= 300) {
						window.cchome.util.logger.error('Links Service', 'Problem with Links Service request', res);
						const linksServiceError = new Error(`${res.statusCode}`);
						reject(linksServiceError);
					}
					else {
						let data = '';
						// set the encoding to UTF8 to prevent decoding issues on certain browsers.
						res.setEncoding('utf8');
						res.on('data', chunk => (data += chunk));
						res.on('end', () => {
							// Success!
							try {
								resolve(JSON.parse(data));
							}
							catch (e) {
								reject(data);
							}
						});
					}
				},
			);

			request.on('error', (err) => {
				// on error, display the search error message
				window.cchome.util.logger.error('Links Service', 'Problem with Links Service request', err.toString());
				const linksServiceError = new Error('_performLinksServiceRequest error');
				reject(linksServiceError);
			});
			request.end();
		});
	}

	_performBookmarkServiceRequest(id: string) {
		return new Promise((resolve, reject) => {
			const request = https.request(
				{
					hostname: getConfig('LINKS_SERVICE'),
					path: `/api/v1/bookmarks/${id}`,
					method: 'POST',
					headers: {
						'x-api-key': getConfig('CLIENT_ID'),
						authorization: `Bearer ${adobeIMS.getAccessToken()}`,
					},
				},
				(res) => {
					if (res.statusCode && res.statusCode >= 300) {
						window.cchome.util.logger.error('Links Service', 'Problem with Links Service request', res);
						const linksServiceError = new Error(`${res.statusCode}`);
						reject(linksServiceError);
					}
					else {
						let data = '';
						// set the encoding to UTF8 to prevent decoding issues on certain browsers.
						res.setEncoding('utf8');
						res.on('data', chunk => (data += chunk));
						res.on('end', () => {
							// Success!
							if (data === '') {
								resolve(true);
							}
							else {
								reject(data);
							}
						});
					}
				},
			);

			request.on('error', (err) => {
				// on error, display the search error message
				window.cchome.util.logger.error('Links Service', 'Problem with Links Service request', err.toString());
				const linksServiceError = new Error('_performLinksServiceRequest error');
				reject(linksServiceError);
			});
			request.end();
		});
	}

	/**
	 * Request copy to library.
	 * @param {id} public asset id
	 * @param {param} prameters for the actions in https://git.corp.adobe.com/pages/CPX/apidocs/#/publinks
	 * @returns {Object}
	 */
	_performPubLinksMonitorServiceRequest(monitorUrl) {
		const parseUrl = url.parse(monitorUrl);
		return new Promise<MonitorDataResponse>((resolve, reject) => {
			const request = https.request(
				{
					hostname: parseUrl.hostname,
					path: parseUrl.path,
					method: 'GET',
					headers: {
						'x-api-key': getConfig('CLIENT_ID'),
						authorization: `Bearer ${adobeIMS.getAccessToken()}`,
					},
				},
				(res) => {
					if (res.statusCode && res.statusCode >= 300) {
						window.cchome.util.logger.error(
							'PubLinksMonitorService',
							'Problem with PubLinksMonitorService request',
							res,
						);
						const linksServiceError = new Error(`${res.statusCode}`);
						reject(linksServiceError);
					}
					else {
						let data = '';
						// set the encoding to UTF8 to prevent decoding issues on certain browsers.
						res.setEncoding('utf8');
						res.on('data', chunk => (data += chunk));
						res.on('end', () => {
							let parsedData = {
								href: '',
								retryAfter: 0,
							};
							// Success!
							try {
								parsedData = JSON.parse(data);
							}
							catch (e) {
								// ParsedData failed, try with cutting the string
								parsedData = JSON.parse(data.slice(data.indexOf('{')));
							}

							const result = {
								res: parsedData,
								statusCode: res.statusCode,
							};
							resolve(result);
						});
					}
				},
			);

			request.on('error', (err) => {
				// on error, display the search error message
				window.cchome.util.logger.error(
					'PubLinksMonitorService',
					'Problem with PubLinksMonitorService request',
					err.toString(),
				);
				const linksServiceError = new Error('_performPubLinksMonitorServiceRequest error');
				reject(linksServiceError);
			});
			request.end();
		});
	}
}

export default LinksService;
